import { Table, Typography } from "antd";
import React from "react";
import { round } from "lodash";

const { Text } = Typography;

const columns = [
  {
    title: "timestamp",
    dataIndex: "timestamp",
    key: "timestamp",
    render: (timestamp) => new Date(timestamp).toLocaleString(),
  },
  {
    title: "symbol",
    dataIndex: "symbol",
    key: "symbol",
  },
  {
    title: "side",
    dataIndex: "side",
    key: "side",
    width: "5%",
  },
  {
    title: "price",
    dataIndex: "price",
    key: "price",
  },
  {
    title: "amount",
    dataIndex: "amount",
    key: "amount",
  },
  {
    title: "cost",
    dataIndex: "cost",
    key: "cost",
  },
  {
    title: "takerOrMaker",
    dataIndex: "takerOrMaker",
    key: "takerOrMaker",
    width: "15%",
  },
  {
    title: "fee",
    dataIndex: "fee",
    key: "fee",
    width: "20%",
    render: ({ cost, currency }) => {
      const costStr = cost.toString();
      if (costStr.includes("e")) {
        // convert scientific notation to decimal, e.g. 1.23e-7 to 0.000000123
        return cost.toFixed(parseInt(costStr.split("e-")[1]) + 1) + " " + currency;
      }
      return `${cost} ${currency}`;
    },
  },
];

const TradeDetailsTable = ({ standardizedTradeData, symbol, side }) => {
  // convert standardized trade data to ccxt Trade object array
  // i.e. { keys: [...], values: [[...], [...], ...] } => [ trade, trade, ...]
  let trades = []; // ccxt Trade object array
  if (standardizedTradeData) {
    const { keys, values } = standardizedTradeData;
    trades = values.map((value) => {
      const trade = {};
      keys.forEach((key, index) => {
        trade[key] = value[index];
      });
      trade.symbol = symbol;
      trade.side = side;
      return trade;
    });
  }

  const dataSource = trades?.map((trade, index) => ({ ...trade, key: index })).sort((a, b) => a.timestamp - b.timestamp);

  // summary calculation
  let totalDurationInMin = 1;
  if (dataSource.length > 1) {
    totalDurationInMin = Math.ceil(Math.abs((dataSource[dataSource.length - 1].timestamp - dataSource[0].timestamp) / 1000 / 60));
  }
  let totalAmount = 0;
  let totalCost = 0;
  let totalTaker = 0;
  let totalMaker = 0;
  let pricePrecision = 0;
  let amountPrecision = 0;
  let feePrecision = {};
  let totalFee = {};
  dataSource.forEach(({ amount, cost, takerOrMaker, price, fee }) => {
    totalAmount += amount;
    totalCost += cost;
    totalMaker += takerOrMaker === "maker" ? amount : 0;
    totalTaker += takerOrMaker === "taker" ? amount : 0;
    pricePrecision = Math.max(pricePrecision, `${price}`.split(".")[1]?.length || 0);
    amountPrecision = Math.max(amountPrecision, `${amount}`.split(".")[1]?.length || 0);
    if (fee.cost.toString().includes("e-")) {
      // scientific notation
      feePrecision[fee.currency] = Math.max(feePrecision[fee.currency] || 0, parseInt(fee.cost.toString().split("e-")[1]) + 1);
    } else {
      feePrecision[fee.currency] = Math.max(feePrecision[fee.currency] || 0, `${fee.cost}`.split(".")[1]?.length || 0);
    }
    totalFee[fee.currency] = (totalFee[fee.currency] || 0) + fee.cost;
  });
  const averagePrice = round(totalCost / totalAmount, pricePrecision);
  totalAmount = round(totalAmount, amountPrecision);
  totalCost = round(totalCost, pricePrecision);
  totalMaker = round(totalMaker, amountPrecision);
  totalTaker = round(totalTaker, amountPrecision);
  Object.keys(feePrecision).forEach((currency) => {
    totalFee[currency] = round(totalFee[currency], feePrecision[currency]);
  });

  return (
    <Table
      bordered
      scroll={{ y: 500, x: "max-content" }}
      dataSource={dataSource}
      columns={columns}
      pagination={{ position: ["bottomRight"], defaultPageSize: 100 }}
      summary={(pageData) => {
        if (pageData.length === 0) {
          return;
        }
        return (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ background: "lightgray" }}>
              <Table.Summary.Cell index={0}>Total Duration</Table.Summary.Cell>
              <Table.Summary.Cell index={1}>Symbol</Table.Summary.Cell>
              <Table.Summary.Cell index={2}>Side</Table.Summary.Cell>
              <Table.Summary.Cell index={3}>Avg Price</Table.Summary.Cell>
              <Table.Summary.Cell index={4}>Total Amount</Table.Summary.Cell>
              <Table.Summary.Cell index={5}>Total Cost</Table.Summary.Cell>
              <Table.Summary.Cell index={6}>Maker Taker Amount</Table.Summary.Cell>
              <Table.Summary.Cell index={7}>Total Fee</Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row style={{ background: "lightgray" }}>
              <Table.Summary.Cell index={0}>{totalDurationInMin} min</Table.Summary.Cell>
              <Table.Summary.Cell index={1}>{dataSource[0]?.symbol}</Table.Summary.Cell>
              <Table.Summary.Cell index={2}>{dataSource[0]?.side}</Table.Summary.Cell>
              <Table.Summary.Cell index={3}>{averagePrice}</Table.Summary.Cell>
              <Table.Summary.Cell index={4}>{totalAmount}</Table.Summary.Cell>
              <Table.Summary.Cell index={5}>{totalCost}</Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                Maker: {totalMaker}, Taker: {totalTaker}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                {Object.entries(totalFee).map(([currency, cost]) => (
                  <Text key={currency}>{`${cost} ${currency}  `}</Text>
                ))}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        );
      }}
    />
  );
};

export default TradeDetailsTable;
